export interface UnitResolveInput {
  // A qty×rate sub-item's unit is simply a free-text label (e.g. "sq ft", "hrs").
  // `unitKey` holds that label directly. Legacy/backfilled rows may still carry
  // the old managed-unit keys ("hours"/"days"/"sq_ft") — normalized below.
  unitKey?: string | null;
  // Carpet/equipment catalog items carry their unit as a catalog `unitLabel`
  // (e.g. "room", "stair"); generic categories leave this null.
  categoryUnitLabel?: string | null;
  rateType?: 'hourly' | 'daily' | null;
}

// The label shown when a row has no explicit unit, keyed by the legacy rateType.
const FALLBACK_LABELS = { hourly: 'hrs', daily: 'days' } as const;

// Bridge the old managed-unit keys to nice labels so pre-existing rows keep
// reading well without a data migration. Free-text labels pass through as-is.
const LEGACY_KEY_LABELS: Record<string, string> = {
  hours: FALLBACK_LABELS.hourly,
  days: FALLBACK_LABELS.daily,
  sq_ft: 'sq ft',
  linear_ft: 'linear ft',
  each: 'each',
  lump_sum: 'lump sum',
};

// Resolve the unit label. Precedence: the sub-item's own label → the
// carpet/equipment catalog unitLabel → the legacy hourly/daily rateType → "hrs".
export function getUnitLabel(input: UnitResolveInput): string {
  const raw = input.unitKey?.trim();
  if (raw) return LEGACY_KEY_LABELS[raw] ?? raw;
  const catLabel = input.categoryUnitLabel?.trim();
  if (catLabel) return catLabel;
  return input.rateType === 'daily' ? FALLBACK_LABELS.daily : FALLBACK_LABELS.hourly;
}

// Common label suggestions for the unit datalist (reuse without a settings page).
export const DEFAULT_UNIT_LABELS = ['hrs', 'days', 'sq ft', 'linear ft', 'each', 'lump sum'];
